import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ChevronRight, Sparkles } from 'lucide-react';

interface TourStep {
  pathMatch: string; // exact pathname or '*'
  target?: string; // CSS selector; if absent the tooltip is centered
  title: string;
  body: ReactNode;
  advance: 'next' | 'click';
}

const STEPS: TourStep[] = [
  {
    pathMatch: '*',
    title: "Let's create a ticket together",
    body: (
      <>
        I'll walk you through every field. <strong>Pick BUY when we get to that step</strong> so you
        see the full flow — sell criteria, buy-more criteria, and the AI-validated checklists.
      </>
    ),
    advance: 'next',
  },
  {
    pathMatch: '/',
    target: '[data-tour="new-ticket-btn"]',
    title: 'Open the ticket form',
    body: 'Click "New ticket" — the tour will follow you to the next page.',
    advance: 'click',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="ticker"]',
    title: 'Ticker',
    body: 'Type a stock ticker like AAPL. Uppercase, max 8 characters.',
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="side"]',
    title: 'BUY or SELL',
    body: (
      <>
        Pick <strong>BUY</strong> for the full demo. SELL tickets only need ticker, shares, and
        reason — they're already the exit, so there's no sell or buy-more checklist.
      </>
    ),
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="shares"]',
    title: 'Shares',
    body: 'Positive integer. The number of shares this ticket covers.',
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="reason"]',
    title: 'Reason / thesis',
    body: 'Min 10 characters. The why — catalyst, valuation, risk. Be honest with future-you.',
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="sell-circumstances"]',
    title: 'Sell circumstances',
    body: (
      <>
        For BUY tickets only. <strong>When would you exit this position?</strong> Stop loss, target
        reached, thesis broken — write it in plain English.
      </>
    ),
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="sell-checklist"]',
    title: 'Sell checklist',
    body: (
      <>
        Add yes/no questions. Each is validated by AI as you blur the input — green check means
        accepted. Try: <em>"Has the stock dropped more than 20% from entry?"</em>
      </>
    ),
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="buy-more-circumstances"]',
    title: 'Buy-more circumstances',
    body: 'When would you scale up? Pullback levels, fundamentals improving, conviction increase.',
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="buy-more-checklist"]',
    title: 'Buy-more checklist',
    body: (
      <>
        Same yes/no format. e.g. <em>"Has the stock pulled back 10% with thesis intact?"</em>
      </>
    ),
    advance: 'next',
  },
  {
    pathMatch: '/tickets/new',
    target: '[data-tour="submit"]',
    title: 'Submit',
    body: (
      <>
        When you click Submit, every active teammate <strong>except you</strong> gets an SMS:
        <pre className="text-[11px] bg-zinc-950 border border-zinc-800 rounded p-2 my-2 whitespace-pre-wrap">{`[Tickets] You BUY 1,000 AAPL - <reason>...
https://app/tickets/<id>`}</pre>
        Tapping the link opens that ticket directly (after sign-in if needed). That's the tour —
        click <strong>Done</strong> and finish the form whenever you're ready.
      </>
    ),
    advance: 'next',
  },
];

export interface TutorialProps {
  active: boolean;
  onExit: () => void;
}

export default function Tutorial({ active, onExit }: TutorialProps) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (active) {
      setStep(0);
      if (location.pathname !== '/') navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const cur = STEPS[step];
  const pathMatches =
    !!cur && (cur.pathMatch === '*' || cur.pathMatch === location.pathname);

  useEffect(() => {
    if (!active || !cur || !pathMatches || !cur.target) {
      setRect(null);
      return;
    }
    const cleanups: Array<() => void> = [];
    let appliedEl: HTMLElement | null = null;

    const apply = (el: HTMLElement) => {
      appliedEl = el;
      el.classList.add('tour-spotlight');
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      const update = () => setRect(el.getBoundingClientRect());
      update();
      window.addEventListener('resize', update);
      window.addEventListener('scroll', update, true);
      cleanups.push(() => {
        window.removeEventListener('resize', update);
        window.removeEventListener('scroll', update, true);
      });
      if (cur.advance === 'click') {
        const onClick = () => setStep((s) => s + 1);
        el.addEventListener('click', onClick);
        cleanups.push(() => el.removeEventListener('click', onClick));
      }
    };

    const tryFind = () => {
      const found = document.querySelector(cur.target!) as HTMLElement | null;
      if (found) {
        apply(found);
        return true;
      }
      return false;
    };

    if (!tryFind()) {
      const interval = setInterval(() => {
        if (tryFind()) clearInterval(interval);
      }, 100);
      cleanups.push(() => clearInterval(interval));
    }

    return () => {
      appliedEl?.classList.remove('tour-spotlight');
      cleanups.forEach((fn) => fn());
    };
  }, [active, step, pathMatches, location.pathname, cur]);

  useEffect(() => {
    if (!active) return;
    if (cur && step >= STEPS.length) {
      onExit();
    }
  }, [active, step, cur, onExit]);

  if (!active || !cur) return null;
  if (!pathMatches) return null;

  const TOOLTIP_W = 320;
  const TOOLTIP_H = 260;
  const PAD = 12;
  let style: React.CSSProperties = {};
  const centered = !cur.target || !rect;

  if (rect && cur.target) {
    let top = rect.bottom + PAD;
    if (top + TOOLTIP_H > window.innerHeight - 16) {
      top = Math.max(16, rect.top - TOOLTIP_H - PAD);
    }
    let left = rect.left;
    if (left + TOOLTIP_W > window.innerWidth - 16) {
      left = Math.max(16, window.innerWidth - TOOLTIP_W - 16);
    }
    style = { top, left, position: 'fixed' };
  }

  const isLast = step === STEPS.length - 1;

  return (
    <>
      {centered && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          onClick={onExit}
        />
      )}
      <div
        className={`z-[100] w-[320px] max-w-[calc(100vw-32px)] card p-4 shadow-xl shadow-black/60 ${
          centered ? 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''
        }`}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 mb-2">
          <div className="w-7 h-7 rounded bg-emerald-600/20 flex items-center justify-center shrink-0">
            <Sparkles size={14} className="text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">
              Step {step + 1} of {STEPS.length}
            </div>
            <h3 className="font-semibold text-sm">{cur.title}</h3>
          </div>
          <button
            onClick={onExit}
            className="text-zinc-500 hover:text-white p-1 -m-1 rounded hover:bg-zinc-800"
            aria-label="Exit tour"
          >
            <X size={14} />
          </button>
        </div>
        <div className="text-xs text-zinc-300 mb-3 leading-relaxed [&_strong]:text-zinc-100">
          {cur.body}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 flex-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-0.5 flex-1 rounded transition-colors ${
                  i <= step ? 'bg-emerald-500' : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
          {cur.advance === 'click' ? (
            <span className="text-[11px] text-emerald-400 italic px-1">click to continue</span>
          ) : isLast ? (
            <button onClick={onExit} className="btn-primary text-xs px-3 py-1">
              Done
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="btn-primary text-xs px-3 py-1"
            >
              Next <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
